// Auth context for the platform admin console — mirrors hooks/useAuth.tsx's
// shape, but deliberately its own provider/context rather than reusing that
// one: an operator session and a tenant staff session are structurally
// separate (see lib/platformApi/session.ts), and this console never needs
// branch switching, module grants, or any of the tenant-specific state
// useAuth carries.
import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { platformApi } from '../lib/platformApi/client';
import {
  getPlatformToken, getPlatformOperator, setPlatformSession, clearPlatformSession,
  type PlatformOperator,
} from '../lib/platformApi/session';

interface PlatformAuthContextValue {
  operator: PlatformOperator | null;
  checking: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const PlatformAuthContext = createContext<PlatformAuthContextValue | null>(null);

export function PlatformAuthProvider({ children }: { children: ReactNode }) {
  const [operator, setOperator] = useState<PlatformOperator | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // No GET /platform-api/auth/me endpoint exists (kept minimal per scope —
    // operators are flat, there's no per-request grant to re-check the way
    // tenant staff's module list can change). Trust the cached operator; a
    // stale/expired token surfaces the first time an actual API call 401s,
    // handled by setPlatformSessionExpiredHandler in client.ts.
    if (getPlatformToken()) setOperator(getPlatformOperator());
    setChecking(false);
  }, []);

  const login = async (username: string, password: string) => {
    const { token, operator: op } = await platformApi.post<{ token: string; operator: PlatformOperator }>(
      '/auth/login', { username, password }
    );
    setPlatformSession(token, op);
    setOperator(op);
  };

  const logout = () => {
    clearPlatformSession();
    setOperator(null);
  };

  return (
    <PlatformAuthContext.Provider value={{ operator, checking, login, logout }}>
      {children}
    </PlatformAuthContext.Provider>
  );
}

export function usePlatformAuth(): PlatformAuthContextValue {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) throw new Error('usePlatformAuth must be used within PlatformAuthProvider');
  return ctx;
}
